import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import type { GGRProductFields, IAdminFeesFilterItem, IAdminFeesForm, IAdminFeesFormGroups, IAdminFeesFormSchema, IAdminFeesSavePayload, IShowAdminFeesResponse } from '@modules/admin-fees/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { FormResult } from '@modules/forms/components/VForm/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES } from '@modules/admin-fees/constants';
import { useSaveAdminFeesMutation } from '@modules/admin-fees/mutations';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

export interface IAdminFeesFormComposable {
  formSchema: ComputedRef<IAdminFeesFormSchema>;
  formGroups: ComputedRef<IAdminFeesFormGroups>;
  isSaveLoading: Ref<boolean>;
  errors: Ref<IApiFormErrors | undefined>;
  clearError(key: string): void;
  onFormChange(
    key: keyof IAdminFeesForm,
    value: IAdminFeesForm[keyof IAdminFeesForm] | undefined,
    result: FormResult<IAdminFeesForm>,
  ): void;
  onSubmit(): Promise<void>;
}

export default function useAdminFeesForm(
  formPayload: Ref<IAdminFeesForm>,
  productsKeyList: Ref<[GGRProductFields, string][]>,
  editFromPermission: Ref<boolean>,
  isShowLoading: Ref<boolean>,
  invalidAdminFeesFilters: Ref<IAdminFeesFilterItem[] | undefined>,
  isEdit: Ref<boolean>,
  adminFeesData: Ref<IShowAdminFeesResponse | undefined>,
): IAdminFeesFormComposable {
  const { getTranslation } = useI18n();
  const { formatMonthDate } = useFormatMonthDate();

  const {
    mutateAsync,
    isLoading: isSaveLoading,
    errors,
    clearError,
  } = useSaveAdminFeesMutation();

  const formSchema = computed<IAdminFeesFormSchema>(() => {
    const schema: IAdminFeesFormSchema = {
      date: {
        type: EFormInputTypes.DATE,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_DATE').value,
        value: formPayload.value?.date,
        required: true,
        view: 'month',
        dateFormat: 'mm.yy',
      },
      admin_fees_merchant_filters: {
        type: EFormInputTypes.SLOT,
        value: formPayload.value?.admin_fees_merchant_filters,
      },
      admin_fees_country_filters: {
        type: EFormInputTypes.SLOT,
      },
    };

    if (productsKeyList.value) {
      for (const [product, shortcode] of productsKeyList.value) {
        schema[product] = {
          type: EFormInputTypes.NUMBER,
          label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, shortcode).value,
          required: true,
          value: formPayload.value?.[product],
          readonly: !editFromPermission.value,
          validation: {
            minValue: {
              message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_LESS_THAN_0').value,
              value: 0,
            },
            maxValue: {
              message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_GREATER_THAN_100').value,
              value: 100,
            },
          },
        };
      }
    }

    return schema;
  });

  const formGroups = computed<IAdminFeesFormGroups>(() => {
    const groups: IAdminFeesFormGroups = [
      {
        items: ['date'],
      },
      {
        items: ['ggr_casino_percent', 'ggr_sportsbook_percent'],
        title: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_GENERAL_CONFIG_SUBHEADER').value,
        minHeight: '185px',
        isLoading: isShowLoading.value,
      },
      {
        items: ['admin_fees_merchant_filters'],
        title: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_BRANDS_SUBHEADER').value,
        minHeight: '85px',
        isLoading: isShowLoading.value,
      },
      {
        items: ['admin_fees_country_filters'],
        title: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_COUNTRY_FEES_SUBHEADER').value,
        minHeight: '195px',
        isLoading: isShowLoading.value,
      },
    ];

    return groups;
  });

  function onFormChange(
    key: keyof IAdminFeesForm,
    value: IAdminFeesForm[keyof IAdminFeesForm] | undefined,
    result: FormResult<IAdminFeesForm>,
  ): void {
    formPayload.value = result.values;
  }

  async function onSubmit(): Promise<void> {
    if (invalidAdminFeesFilters.value?.length || !editFromPermission.value)
      return;

    const productValues = productsKeyList.value.reduce((acc, [product]) => {
      acc[product] = formPayload.value[product];
      return acc;
    }, {} as Partial<Record<GGRProductFields, number>>);

    const payload: IAdminFeesSavePayload = {
      isEdit: isEdit.value,
      history_of_admin_fees_change: {
        ...productValues,
        id: adminFeesData.value?.history_of_admin_fees_change.id,
        date: formatMonthDate(formPayload.value.date),
        admin_fees_merchant_filters_attributes: formPayload.value.admin_fees_merchant_filters.map(({
          errors,
          isAllMerchants,
          admin_fees_country_filters,
          ...rest
        }) => ({
          ...rest,
          admin_fees_country_filters_attributes: admin_fees_country_filters.map(({ isAllCountries, ...item }) => ({
            ...item,
          })),
        })),
      },
    };

    await mutateAsync(payload);
  }

  return {
    formSchema,
    formGroups,
    isSaveLoading,
    errors,
    clearError,
    onFormChange,
    onSubmit,
  };
}
